/**
 * StatisticDashboardPresenter
 * --
 */
import React, { useRef, useState, useEffect } from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  Statistic,
  ModalLayout,
  Descriptions,
} from 'components';
import { Button } from 'antd';
import { stringToMoneyFormat } from 'utils';
import {
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Tooltip,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import moment from 'moment';

const COLORS = ['#2EBF6D', '#EFC563', '#E16070', '#2C97DE', '#675cb4'];

const StatisticDashboardPresenter = () => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
    sales: {
      fontSize: 16,
      paddingLeft: 20,
      width: 150,
    },
  };

  const menu = [
    { name: '아메리카노', price: 5000 },
    { name: '카페 라떼', price: 7000 },
    { name: '바닐라 라떼', price: 8000 },
    { name: '초코 라떼', price: 8500 },
    { name: '아이스티', price: 6000 },
  ];
  const chartData = [1, 2, 3, 4, 5, 6, 7].reduce((acc, cur, _index) => {
    const today = new Date();
    const day = new Date(today.setDate(today.getDate() - (_index + 1)));

    const m = menu.reduce((menuAcc, item) => {
      menuAcc[item.name] = Math.floor(Math.random() * 1000 + 1);
      return menuAcc;
    }, {});

    const d = {
      day: day,
      ...m,
    };

    return acc.concat(d);
  }, []);

  const descriptionsItems = [
    {
      key: `1`,
      label: '통계번호',
      children: '000000',
      span: 2,
    },
    {
      key: `2`,
      label: '정산일',
      children: moment().format('YYYY년 MM월 DD일 HH:mm:ss'),
      span: 2,
    },
    {
      key: `3`,
      label: '통계 명',
      children: '판매 통계',
      span: 2,
    },
    {
      key: `4`,
      label: '총 판매 수치',
      children: '123,456,789',
    },
    {
      key: `5`,
      label: '총 판매 상품',
      children: '123,456',
    },
  ];

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  useEffect(() => {
    const call = (e) => {
      setChartWidth(chartRef.current.clientWidth);
    };

    if (chartRef.current) call();

    window.addEventListener('resize', call);

    return () => {
      window.removeEventListener('resize', call);
    };
  }, [chartRef]);

  /* ===== RENDER ===== */
  return (
    <>
      <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
        <Row>
          <Col x={24}>
            <Title style={{ ...styles.title }}>대시보드</Title>

            {/* 요약 */}
            <Content
              maxWidth={'100%'}
              padding={0}
              backgroundColor={'none'}
              style={{ marginBottom: 40, display: 'flex' }}
            >
              {[
                { title: '총 회원 수', value: 123111111 },
                { title: '총 판매 수치', value: 124132111 },
                { title: '총 매출', value: 234146111 },
                { title: '총 상품', value: 433111 },
              ].map((_item, _index) => (
                <div
                  key={`${_item.label}-${_index}`}
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 4,
                    width: `calc(100% / 4)`,
                    marginRight: _index === 3 ? 0 : 4,
                    padding: 20,
                  }}
                >
                  <Statistic
                    title={_item.title}
                    value={stringToMoneyFormat(_item.value)}
                    direction={'row'}
                    style={{ justifyContent: 'center' }}
                  />
                </div>
              ))}
            </Content>
            {/* 요약 끝 */}

            <Row gutter={[20, 20]}>
              <Col x={12}>
                <Content maxWidth={'100%'} borderRadius={4}>
                  <div ref={chartRef}>
                    <BarChart
                      width={chartWidth}
                      height={300}
                      data={chartData}
                      onClick={() => setIsDrawerOpened(!isDrawerOpened)}
                    >
                      <XAxis
                        dataKey={'day'}
                        stroke="#686C73"
                        tickFormatter={(d) => moment(d).format('MM-DD')}
                      />
                      <YAxis stroke="#686C73" />
                      <Tooltip />
                      {menu.map((_item, _index) => (
                        <Bar dataKey={_item.name} fill={COLORS[_index]} />
                      ))}
                    </BarChart>
                  </div>
                </Content>
                <div>
                  {menu.map((_item, _index) => (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 10,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            background: COLORS[_index],
                          }}
                        ></div>
                        <p style={{ ...styles.sales }}>{_item.name}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <p style={{ ...styles.sales }}>
                          {moment(chartData[0].day).format('YYYY-MM-DD')}
                        </p>
                        <p
                          style={{
                            color:
                              chartData[0][_item.name] -
                                chartData[1][_item.name] >
                              0
                                ? '#2044FF'
                                : '#FF2099',
                            ...styles.sales,
                          }}
                        >
                          {chartData[0][_item.name] - chartData[1][_item.name] >
                          0
                            ? '+'
                            : '-'}
                          {stringToMoneyFormat(
                            Math.abs(
                              (chartData[0][_item.name] -
                                chartData[1][_item.name]) *
                                _item.price
                            ),
                            '원'
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>

              <Col x={12}>
                <Content maxWidth={'100%'} borderRadius={4}>
                  <AreaChart width={chartWidth} height={300} data={chartData}>
                    <XAxis
                      dataKey={'day'}
                      stroke="#686C73"
                      tickFormatter={(d) => moment(d).format('MM-DD')}
                    />
                    <YAxis stroke="#686C73" />
                    <Tooltip />
                    {menu.map((_item, _index) => (
                      <Area dataKey={_item.name} fill={COLORS[_index]} />
                    ))}
                  </AreaChart>
                </Content>
                <div>
                  {menu.map((_item, _index) => (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 10,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            background: COLORS[_index],
                          }}
                        ></div>
                        <p style={{ ...styles.sales }}>{_item.name}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <p style={{ ...styles.sales }}>
                          {moment(chartData[0].day).format('YYYY-MM-DD')}
                        </p>
                        <p
                          style={{
                            color:
                              chartData[0][_item.name] -
                                chartData[1][_item.name] >
                              0
                                ? '#2044FF'
                                : '#FF2099',
                            ...styles.sales,
                          }}
                        >
                          {chartData[0][_item.name] - chartData[1][_item.name] >
                          0
                            ? '+'
                            : '-'}
                          {stringToMoneyFormat(
                            Math.abs(
                              (chartData[0][_item.name] -
                                chartData[1][_item.name]) *
                                _item.price
                            ),
                            '원'
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>

              <Col x={12}>
                <Content maxWidth={'100%'} borderRadius={4}>
                  <PieChart width={chartWidth} height={300}>
                    <Pie
                      data={menu.map((item) => ({
                        name: item,
                        sales: Math.floor(Math.random() * 1000 + 1),
                      }))}
                      dataKey={'sales'}
                      nameKey={'name'}
                    >
                      {menu.map((_, _index) => (
                        <Cell key={`cell-${_index}`} fill={COLORS[_index]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </Content>
              </Col>

              <Col x={12}>
                <Content maxWidth={'100%'} borderRadius={4}>
                  <LineChart width={chartWidth} height={300} data={chartData}>
                    <XAxis
                      dataKey={'day'}
                      stroke="#686C73"
                      tickFormatter={(d) => moment(d).format('MM-DD')}
                    />
                    <YAxis stroke="#686C73" />
                    <Tooltip />
                    {menu.map((_item, _index) => (
                      <Line dataKey={_item.name} stroke={COLORS[_index]} />
                    ))}
                  </LineChart>
                </Content>
              </Col>
            </Row>
          </Col>
        </Row>
      </Content>
      {/* Drawer */}
      <ModalLayout
        type={'drawer'}
        open={isDrawerOpened}
        onCancel={setIsDrawerOpened}
        centered
        title={`통계 상세정보`}
        width={'43%'}
        footer={
          <Content
            maxWidth={'100%'}
            padding={10}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <Button onClick={() => setIsDrawerOpened(false)}>닫기</Button>
            </div>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={0}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <h4
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#404040',
                    marginBottom: 20,
                  }}
                >
                  통계정보
                </h4>
                <Descriptions items={descriptionsItems} />
              </Content>

              <Content maxWidth={'100%'} padding={0} style={{ marginTop: 40 }}>
                <Col x={12}>
                  <Content maxWidth={'100%'} borderRadius={4} padding={0}>
                    <LineChart width={chartWidth} height={300} data={chartData}>
                      <XAxis
                        dataKey={'day'}
                        stroke="#686C73"
                        tickFormatter={(d) => moment(d).format('MM-DD')}
                      />
                      <YAxis stroke="#686C73" />
                      <Tooltip />
                      {menu.map((_item, _index) => (
                        <Line dataKey={_item.name} stroke={COLORS[_index]} />
                      ))}
                    </LineChart>
                  </Content>
                </Col>

                <div style={{ paddingTop: 20 }}>
                  {menu.map((_item, _index) => (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 10,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            background: COLORS[_index],
                          }}
                        ></div>
                        <p style={{ ...styles.sales }}>{_item.name}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <p style={{ ...styles.sales }}>
                          {moment(chartData[0].day).format('YYYY-MM-DD')}
                        </p>
                        <p
                          style={{
                            color:
                              chartData[0][_item.name] -
                                chartData[1][_item.name] >
                              0
                                ? '#2044FF'
                                : '#FF2099',
                            ...styles.sales,
                          }}
                        >
                          {chartData[0][_item.name] - chartData[1][_item.name] >
                          0
                            ? '+'
                            : '-'}
                          {stringToMoneyFormat(
                            Math.abs(
                              (chartData[0][_item.name] -
                                chartData[1][_item.name]) *
                                _item.price
                            ),
                            '원'
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>
    </>
  );
};

export default StatisticDashboardPresenter;
